import type { AsyncStatus, ScheduleFlow } from 'lib-shared-types-api';
import { useEffect, useState } from 'react';

import { flowsApi } from 'apis/flows';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

type UseScheduleFlowsResult = {
  /** The async status of the read flows schedule flows api endpoint. */
  readonly asyncStatus: AsyncStatus;
  /** The current page schedule flows. */
  readonly currentPageFlows: readonly ScheduleFlow[];
  /** Whether there is a next page to navigate to. */
  readonly hasNextPage: boolean;
  /** Whether there is a previous page to navigate to. */
  readonly hasPreviousPage: boolean;
  /** The method for navigating to the next page. */
  readonly nextPage: VoidFunction;
  /** The method for navigating to the previous page. */
  readonly previousPage: VoidFunction;
  /** The method for refetching schedule flows. */
  readonly refetchItems: VoidFunction;
};

/**
 * A hook that handles fetching and paginating schedule flows on the business
 * hours schedule flows overview page.
 */
export function useScheduleFlows(
  scheduleId: string,
  limit = 50,
): UseScheduleFlowsResult {
  const [currentPageFlows, setCurrentPageFlows] = useState<
    readonly ScheduleFlow[]
  >([]);

  const {
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items,
    nextPage,
    previousPage,
    refetchItems,
  } = usePaginatedEndpoint({
    endpoint: flowsApi.endpoints.readFlowsSchedulesFlows,
    initialArg: { id: scheduleId, limit },
    itemsSelector: ({ data: { flows } }) => flows,
  });

  // Updates local state of current page schedule flows that is used for
  // populating the table.
  useEffect(() => {
    setCurrentPageFlows(items);
  }, [items]);

  return {
    asyncStatus,
    currentPageFlows,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    refetchItems,
  };
}
